// The administration's manual on screen (P3 F4c-4). `docs/fiscal/MANUEL.fr.md` is written in a small, fixed subset of
// Markdown — headings, paragraphs, lists, tables, indented code, `code` and **bold** — and this renders exactly that
// subset as React elements: no HTML string, nothing in the text can become markup. A construct outside the subset
// shows as plain text, which a test on the real manual guards against.

import { Fragment, type ReactNode } from 'react';

export type ManualBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'table'; head: string[]; rows: string[][] }
  | { kind: 'code'; text: string };

const cells = (line: string) =>
  line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

/** The manual as blocks. Pure, so the real manual can be checked to parse into nothing unexpected. */
export function parseManual(md: string): ManualBlock[] {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: ManualBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === '') {
      i++;
      continue;
    }
    const h = /^(#{1,3}) (.*)$/.exec(line);
    if (h) {
      out.push({ kind: 'heading', level: h[1]!.length as 1 | 2 | 3, text: h[2]! });
      i++;
      continue;
    }
    if (line.startsWith('    ')) {
      const code: string[] = [];
      while (i < lines.length && (lines[i]!.startsWith('    ') || lines[i]!.trim() === '')) code.push(lines[i++]!.slice(4));
      out.push({ kind: 'code', text: code.join('\n').trimEnd() });
      continue;
    }
    if (line.startsWith('|')) {
      const head = cells(line);
      i += 2; // the header and its separator
      const rows: string[][] = [];
      while (i < lines.length && lines[i]!.startsWith('|')) rows.push(cells(lines[i++]!));
      out.push({ kind: 'table', head, rows });
      continue;
    }
    const bullet = /^- (.*)$/.exec(line);
    const number = /^\d+\. (.*)$/.exec(line);
    if (bullet || number) {
      const ordered = !!number;
      const items: string[] = [];
      while (i < lines.length) {
        const m = ordered ? /^\d+\. (.*)$/.exec(lines[i]!) : /^- (.*)$/.exec(lines[i]!);
        if (m) items.push(m[1]!);
        else if (/^ {2,}\S/.test(lines[i]!) && items.length) items[items.length - 1] += ` ${lines[i]!.trim()}`;
        else break;
        i++;
      }
      out.push({ kind: 'list', ordered, items });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() !== '' && !/^(#{1,3} |\||- |\d+\. | {4})/.test(lines[i]!)) para.push(lines[i++]!.trim());
    out.push({ kind: 'paragraph', text: para.join(' ') });
  }
  return out;
}

/** `code` and **bold**, nothing else. */
export function inline(text: string): ReactNode {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('`') && p.endsWith('`') && p.length > 1 ? <code key={i}>{p.slice(1, -1)}</code>
    : p.startsWith('**') && p.endsWith('**') && p.length > 3 ? <strong key={i}>{p.slice(2, -2)}</strong>
    : <Fragment key={i}>{p}</Fragment>,
  );
}

export function ManualView({ markdown }: { markdown: string }) {
  return (
    <div className="pms-fiscal-manual">
      {parseManual(markdown).map((b, i) => {
        switch (b.kind) {
          case 'heading':
            return b.level === 1 ? <h3 key={i}>{inline(b.text)}</h3> : b.level === 2 ? <h4 key={i}>{inline(b.text)}</h4> : <h5 key={i}>{inline(b.text)}</h5>;
          case 'paragraph':
            return <p key={i}>{inline(b.text)}</p>;
          case 'code':
            return <pre key={i}>{b.text}</pre>;
          case 'list':
            return b.ordered
              ? <ol key={i}>{b.items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ol>
              : <ul key={i}>{b.items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ul>;
          case 'table':
            return (
              <div key={i} className="pms-table-wrap">
                <table className="pms-table">
                  <thead><tr>{b.head.map((c, j) => <th key={j}>{inline(c)}</th>)}</tr></thead>
                  <tbody>{b.rows.map((r, j) => <tr key={j}>{r.map((c, k) => <td key={k}>{inline(c)}</td>)}</tr>)}</tbody>
                </table>
              </div>
            );
        }
      })}
    </div>
  );
}
